import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';

const formatCurrency = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const formatCompetencia = (comp: number) => {
  const s = String(comp);
  return `${s.substring(4, 6)}/${s.substring(0, 4)}`;
};

const EspecializadaCustos: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getCustos(municipioAtivo.id, dataInicio || undefined, dataFim || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const evolucaoData = useMemo(() => {
    if (!data?.evolucaoMensal) return [];
    return data.evolucaoMensal.map((e: any) => ({
      comp: formatCompetencia(e.anomescompetencia),
      planejado: Number(e.valor_planejado),
      realizado: Number(e.valor_realizado),
    }));
  }, [data]);

  const totalPlanejado = Number(data?.kpis?.total_planejado || 0);
  const totalRealizado = Number(data?.kpis?.total_realizado || 0);
  const variacao = totalPlanejado > 0 ? ((totalRealizado - totalPlanejado) / totalPlanejado * 100).toFixed(1) : '0';

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    return `Custos Especializada - ${municipioAtivo?.nome || 'Contagem/MG'}
Total planejado: ${formatCurrency(totalPlanejado)}
Total realizado: ${formatCurrency(totalRealizado)}
Variacao: ${variacao}%
Procedimentos pactuados: ${data.kpis?.procedimentos_pactuados || 0}
Procedimentos realizados: ${data.kpis?.procedimentos_realizados || 0}`;
  }, [data, totalPlanejado, totalRealizado, variacao, municipioAtivo]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Análise de Custos</Typography>
          <Typography variant="body2" color="text.secondary">Comparativo planejado vs realizado por procedimento</Typography>
        </Box>
        <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Custo Planejado" valor={formatCurrency(totalPlanejado)} cor="#2563eb" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Custo Realizado" valor={formatCurrency(totalRealizado)} cor="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            titulo="Variação"
            valor={`${Number(variacao) > 0 ? '+' : ''}${variacao}%`}
            cor={Number(variacao) > 0 ? '#ef4444' : '#16a34a'}
          />
        </Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Procedimentos" valor={`${data?.kpis?.procedimentos_realizados || 0} / ${data?.kpis?.procedimentos_pactuados || 0}`} cor="#7c3aed" /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Evolução Mensal */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Evolução Mensal: Planejado vs Realizado</Typography>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={evolucaoData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="comp" fontSize={10} angle={-45} textAnchor="end" height={50} />
                <YAxis fontSize={11} tickFormatter={(v: any) => `R$${(v/1000000).toFixed(1)}M`} />
                <Tooltip formatter={(v) => formatCurrency(Number(v))} />
                <Legend />
                <Line type="monotone" dataKey="planejado" name="Planejado" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="realizado" name="Realizado" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Custos Procedimentos */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Top 20 Procedimentos por Custo</Typography>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={(data?.topCustosProcedimentos || []).slice(0, 15).map((p: any) => ({
                nome: (p.nome_procedimento || '').substring(0, 30),
                valor: Number(p.total_valor),
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => `R$${(v/1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="nome" width={220} fontSize={9} />
                <Tooltip formatter={(v) => [formatCurrency(Number(v)), "Custo"]} />
                <Bar dataKey="valor" fill="#ea580c" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Tabela Comparativa */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Comparativo por Procedimento</Typography>
            <TableContainer sx={{ maxHeight: 450 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Planejado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Realizado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Variação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.comparativoProcedimentos || []).slice(0, 30).map((p: any, i: number) => {
                    const vPct = p.variacao_pct !== null ? Number(p.variacao_pct) : null;
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{(p.procedimento || '').substring(0, 40)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(Number(p.valor_planejado))}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(Number(p.valor_realizado))}</TableCell>
                        <TableCell align="right">
                          {vPct !== null ? (
                            <Chip size="small" label={`${vPct > 0 ? '+' : ''}${vPct}%`}
                              color={vPct > 10 ? 'error' : vPct < -10 ? 'success' : 'default'}
                              variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaCustos;
